import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles, Popover } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import {
  discardEmployeeAiImport,
  fetchEmployeeAiImport,
} from "../../../../Stores/Employee/actions";
import { CcTooltip } from "../../../../design-system/carecentre";
import ConfirmModal from "../../../../Components/ConfirmModal";

const EMPLOYEE_IMPORT_PATH = "/d/hr/employee/import-ai";
const USER_IMPORT_PATH = "/d/users/import-ai";

const isImportPath = (pathname) =>
  pathname === EMPLOYEE_IMPORT_PATH || pathname === USER_IMPORT_PATH;

const reviewPathFor = (module) =>
  module === "user" ? USER_IMPORT_PATH : EMPLOYEE_IMPORT_PATH;

const useStyles = makeStyles(() => ({
  wrap: {
    display: "inline-flex",
    alignItems: "center",
    flex: "none",
    lineHeight: 0,
  },
  trigger: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    width: 32,
    height: 32,
    padding: 0,
    border: 0,
    borderRadius: "50%",
    background: "transparent",
    cursor: "pointer",
    color: "#2563eb",
    flex: "none",
    "&:hover": { background: "rgba(37,99,235,0.08)" },
  },
  triggerReady: {
    background: "#eef3ff",
    "&:hover": { background: "#d6e0fb" },
  },
  triggerBusy: { color: "#174ea6" },
  icon: { fontSize: 22 },
  dot: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#2563eb",
    boxShadow: "0 0 0 2px #fff",
  },
  spin: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: "2px solid #d6e0fb",
    borderTopColor: "#2563eb",
    animation: "$spin 0.8s linear infinite",
  },
  "@keyframes spin": {
    to: { transform: "rotate(360deg)" },
  },
  paper: {
    width: 340,
    padding: "14px 14px 12px",
    borderRadius: 12,
    border: "1px solid #e6e8ef",
    boxShadow: "0 10px 30px rgba(10,10,41,0.14)",
    fontFamily: '"Mulish", system-ui, sans-serif',
    color: "#0a0a29",
  },
  title: {
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.35,
  },
  meta: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 12,
  },
  ghost: {
    height: 34,
    padding: "0 12px",
    borderRadius: 9,
    fontWeight: 800,
    fontSize: 12,
    background: "#fff",
    border: "1px solid #e6e8ef",
    cursor: "pointer",
    color: "#0a0a29",
    "&:hover": { borderColor: "#d6e0fb" },
  },
  primary: {
    height: 34,
    padding: "0 12px",
    borderRadius: 9,
    fontWeight: 800,
    fontSize: 12,
    background: "#2563eb",
    color: "#fff",
    border: 0,
    cursor: "pointer",
  },
}));

const EmployeeAiImportDock = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const token = useSelector((state) => state.AuthReducer.token);
  const aiImport = useSelector((state) => state.EmployeeReducer.aiImport);
  const locationId = useSelector(
    (state) => state.SlickCareProfilesReducer?.activeLocation?.id,
  );

  // One status call, for the chip and nothing else — an import belongs to the
  // home it was uploaded on, so switching home asks again.
  useEffect(() => {
    if (!token || isImportPath(location.pathname)) return undefined;
    const tick = () => dispatch(fetchEmployeeAiImport());
    tick();
    const busy =
      aiImport?.status === "queued" || aiImport?.status === "processing";
    const id = window.setInterval(tick, busy ? 2000 : 8000);
    return () => window.clearInterval(id);
  }, [dispatch, token, locationId, location.pathname, aiImport?.status]);

  useEffect(() => {
    if (!aiImport?.active) setOpen(false);
  }, [aiImport?.active]);

  if (!aiImport?.active) return null;
  if (isImportPath(location.pathname)) return null;
  if (
    aiImport.status !== "queued" &&
    aiImport.status !== "processing" &&
    aiImport.status !== "ready"
  ) {
    return null;
  }

  const busy =
    aiImport.status === "queued" || aiImport.status === "processing";
  const ready = aiImport.status === "ready";

  const title = busy
    ? `Processing “${aiImport.filename}”`
    : `“${aiImport.filename}” is ready to review`;
  const meta = busy
    ? "You can keep working. Open this to review or discard it."
    : aiImport.row_count
      ? `${aiImport.row_count} staff rows mapped`
      : "Open it to create employees, or discard the file.";

  return (
    <span ref={anchorRef} className={classes.wrap}>
      <CcTooltip title={open ? "" : title} placement="bottom">
        <button
          type="button"
          className={`${classes.trigger} ${
            busy ? classes.triggerBusy : classes.triggerReady
          }`}
          aria-label={title}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          {busy ? (
            <span className={classes.spin} />
          ) : (
            <InfoIcon className={classes.icon} />
          )}
          {!busy ? <span className={classes.dot} /> : null}
        </button>
      </CcTooltip>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: classes.paper, role: "dialog" }}
      >
        <div className={classes.title}>{title}</div>
        <div className={classes.meta}>{meta}</div>
        <div className={classes.actions}>
          <button
            type="button"
            className={classes.ghost}
            onClick={() => {
              setOpen(false);
              setConfirmDiscard(true);
            }}
          >
            Discard
          </button>
          {ready || busy ? (
            <button
              type="button"
              className={classes.primary}
              onClick={() => {
                setOpen(false);
                history.push(reviewPathFor(aiImport.module));
              }}
            >
              {ready ? "Review" : "Open"}
            </button>
          ) : null}
        </div>
      </Popover>
      <ConfirmModal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          dispatch(discardEmployeeAiImport());
        }}
        variant="danger"
        title="Discard this import?"
        description={
          aiImport.filename
            ? `“${aiImport.filename}” will be removed. You’ll need to upload again.`
            : "This file will be removed. You’ll need to upload again."
        }
        btnName="Discard"
      />
    </span>
  );
};

export default EmployeeAiImportDock;
